import { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useSearchParams } from "react-router-dom";
import { Loader2, Clock, MailCheck, XCircle } from "lucide-react";
import { api, money, errMsg } from "@/lib/api";

const STATUS = {
  pending: { icon: Clock, color: "text-amber-400", title: "Awaiting verification", text: "We're checking your transaction on the blockchain. Your code will be emailed once confirmed." },
  delivered: { icon: MailCheck, color: "text-emerald-400", title: "Code sent to your email", text: "Your activation code has been emailed. Check your inbox and spam folder." },
  awaiting_payment: { icon: Clock, color: "text-sky-400", title: "Waiting for your transaction ID", text: "Submit your transaction ID on the checkout page before the 5-minute timer ends." },
  expired: { icon: XCircle, color: "text-gray-400", title: "Payment window expired", text: "No transaction ID was submitted within 5 minutes, so this order was not counted as paid. If you already paid, message us in the chat with your transaction ID." },
  rejected: { icon: XCircle, color: "text-red-400", title: "Payment could not be verified", text: "We couldn't confirm this payment. Contact us with your order ID." },
};

const Result = ({ order }) => {
  const s = STATUS[order.status];
  return (
    <div data-testid="order-lookup-result" className="rounded-xl border border-white/10 bg-[#0F1117] p-6 sm:p-8 space-y-5 rise">
      <div className="flex items-start gap-4">
        <s.icon className={`${s.color} shrink-0`} size={28} />
        <div>
          <p data-testid="order-status" className={`font-display font-semibold text-lg ${s.color}`}>{s.title}</p>
          <p className="text-sm text-gray-400 mt-1">{s.text}</p>
          {order.admin_note && <p data-testid="order-admin-note" className="text-sm text-gray-300 mt-3 border-l-2 border-white/20 pl-3">{order.admin_note}</p>}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <div><p className="label-eyebrow text-gray-500">Order</p><p className="font-mono mt-1">{order.id}</p></div>
        <div><p className="label-eyebrow text-gray-500">Network</p><p className="font-mono mt-1">USDT · {order.network}</p></div>
        <div><p className="label-eyebrow text-gray-500">Total</p><p className="font-mono mt-1">{money(order.total)}</p></div>
      </div>
      <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
        {order.items.map((i) => <div key={i.game_id} className="flex justify-between"><span>{i.name} <span className="text-gray-500">({i.platform}) × {i.qty}</span></span><span className="font-mono">{money(i.price * i.qty)}</span></div>)}
      </div>
    </div>
  );
};

export default function OrderStatus() {
  useSEO({ title: "Track Your Order", path: "/order" });
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.get("/orders/lookup", { params: { order_id: id, email } });
      setOrder(r.data);
    } catch (err) {
      setOrder(null); setError(errMsg(err, "Order not found"));
    } finally { setBusy(false); }
  };

  useEffect(() => { if (id && email) lookup(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const inputCls = "w-full h-11 px-3 rounded-md bg-[#08090C] border border-white/10 focus:outline-none focus:border-emerald-500/60";
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 pt-12 space-y-6">
      <p className="label-eyebrow text-emerald-400">Track order</p>
      <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl">Where's my key?</h1>
      <form onSubmit={lookup} className="rounded-xl border border-white/10 bg-[#0F1117] p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input data-testid="order-lookup-input" required value={id} onChange={(e) => setId(e.target.value)} placeholder="Order ID (GR-...)" className={`${inputCls} font-mono`} />
        <input data-testid="order-lookup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used" className={inputCls} />
        <button data-testid="order-lookup-submit" disabled={busy} className="h-11 px-5 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-400 disabled:opacity-50 transition-colors">
          {busy && <Loader2 size={16} className="animate-spin" />} Check
        </button>
      </form>
      {error && <p data-testid="order-lookup-error" className="text-red-400 text-sm">{error}</p>}
      {order && <Result order={order} />}
    </div>
  );
}
